import '../../styles/restScreens.css';
import React, { useState, useMemo } from 'react';
import ProgressBar from './ProgressBar';

// ============================================================
// TaskListTable — sortable list view.
// v1 scope note: the legacy VWH GUI supported per-user drag-resizable
// columns (persisted to localStorage). This port keeps sortable columns
// with a fixed, sensible width set + horizontal scroll instead of
// drag-resize, to keep the first pass tight — resize can be added later
// as a follow-up if Val wants it back.
// ============================================================

const COLUMNS = [
  { key: 'task_name', label: 'Task', width: 'min-w-[220px]' },
  { key: 'workspace_tag', label: 'Brand', width: 'w-24' },
  { key: 'status', label: 'Status', width: 'w-28' },
  { key: 'priority', label: 'Priority', width: 'w-24' },
  { key: 'flag', label: 'Flag', width: 'w-20' },
  { key: 'progress_pct', label: 'Progress', width: 'w-32' },
  { key: 'bucket_name', label: 'Bucket', width: 'w-32' },
  { key: 'phase', label: 'Phase', width: 'w-40' },
  { key: 'quarter', label: 'Quarter', width: 'w-20' },
  { key: 'due_date', label: 'Due', width: 'w-28' },
];

export default function TaskListTable({ tasks, theme = 'light', onSelectTask }) {
  const [sortKey, setSortKey] = useState('due_date');
  const [sortDir, setSortDir] = useState('asc');

  const sorted = useMemo(() => {
    const copy = [...tasks];
    copy.sort((a, b) => {
      const av = a[sortKey] ?? '';
      const bv = b[sortKey] ?? '';
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return copy;
  }, [tasks, sortKey, sortDir]);

  function toggleSort(key) {
    if (sortKey === key) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  }

  const headerBg = theme === 'dark' ? 'bg-gray-800 text-gray-300' : 'bg-gray-50 text-gray-600';
  const rowBorder = theme === 'dark' ? 'border-gray-700' : 'border-gray-200';
  const rowHover = theme === 'dark' ? 'hover:bg-gray-700/50' : 'hover:bg-[var(--accent-ink)]/10';

  if (tasks.length === 0) {
    return (
      <div data-m6-layout="tasks" data-m6-component="TaskListTable" className={`text-center py-12 text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
        No tasks match the current filters.
      </div>
    );
  }

  return (
    <div tabIndex={0} role="region" aria-label="Task list, scroll for more columns" data-m6-layout="tasks" data-m6-component="TaskListTable" className="overflow-x-auto rounded-lg border" style={{ borderColor: theme === 'dark' ? '#374151' : '#e5e7eb' }}>
      <table className="w-full text-sm">
        <thead>
          <tr className={headerBg}>
            {COLUMNS.map(col => (
              <th
                key={col.key}
                onClick={() => toggleSort(col.key)}
                className={`text-left font-semibold px-3 py-2 cursor-pointer select-none ${col.width}`}
              >
                {col.label} {sortKey === col.key && (sortDir === 'asc' ? '▲' : '▼')}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map(task => (
            <tr
              key={task.id}
              onClick={() => onSelectTask(task)}
              className={`border-t cursor-pointer ${rowBorder} ${rowHover}`}
            >
              <td className={`px-3 py-2 font-medium ${theme === 'dark' ? 'text-gray-100' : 'text-gray-800'}`}>{task.task_name}</td>
              <td className={`px-3 py-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>{task.workspace_tag}</td>
              <td className={`px-3 py-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>{task.status}</td>
              <td className={`px-3 py-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>{task.priority || '—'}</td>
              <td className={`px-3 py-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                {task.flag && task.flag !== 'None' ? task.flag : '—'}
              </td>
              <td className="px-3 py-2"><ProgressBar progressPct={task.progress_pct} theme={theme} showLabel={false} size="sm" /></td>
              <td className={`px-3 py-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>{task.bucket_name || '—'}</td>
              <td className={`px-3 py-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>{task.phase || '—'}</td>
              <td className={`px-3 py-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>{task.quarter || '—'}</td>
              <td className={`px-3 py-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>{task.due_date || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
